import {Dynamic} from "./channel/DynamicDataParser.ts";
import {Subscription} from "./channel/Subscription.ts";
import type {CSSProperties} from "vue";

export type HideFlags = {
    data: boolean,
    rule: boolean,
    render: boolean,
    alternating: boolean,
    image: boolean,
    anchoring: boolean,
    dedup: boolean,
    // Main-thread staged: server replied "Playlist not found."
    playlist: boolean,
    // Main-thread staged: this countdown's `countdown_props.countdown_hide` condition holds
    // (see docs/EXTRACTED_COMMENTS.md#c759).
    countdown: boolean,
    // Main-thread staged BY THE PARENT: an enclosing DataObjectTimer's criteria says this child
    // must not show. Staged on the timer's children, never on the timer itself
    // (see docs/EXTRACTED_COMMENTS.md#c777).
    timer: boolean,
    // Renderer-owned: "this item's HIDE_WHEN_*CHILDREN condition holds". Computed from scratch
    // every RenderWorker tick and published via HIDE_FLAGS_COMMIT like render/anchoring/dedup.
    // The main thread must never set it.
    children: boolean,
};


export interface ComponentList {
    [key: string]: Component;
}

export type Cluster = {
    enabled: boolean;
    row_start: number;
    channel: number;
}

export type Component = {
    object_name: string,
    type: string,
    dynamic: Dynamic,
    sizing: string,
    anchor: Anchor | null,
    parent: string,
    props: {
        id: string,
        style: any,
        key: string,
        class?: string,
    },
    published: {
        hide_flags: HideFlags,
        src: string
    },
    flags: {
      updated_data: boolean,
      // see docs/EXTRACTED_COMMENTS.md#c156
      updated_position: boolean,
      // see docs/EXTRACTED_COMMENTS.md#c157
      geometry_settled?: boolean,
      calculated_row: boolean,
      pending_src?: boolean,
      is_datasheet: boolean,
      template_resolved?: boolean,
      is_cluster_sheet: boolean,
    },
    children: Component[],
    hide_flags: HideFlags,
    button_props: {
      button_type: ButtonType,
      select_page: string,
      disabled_colors: SelectionColors,
      pressed_colors: SelectionColors,
      selection_colors: SelectionColors,
      disabled: boolean,
      state_style: StateStyle[],
      css_style?: string,
    },
    submit_button_props: {
        button_type: ButtonType,
        select_page: string,
        objects: SubmitionObject[],
        disabled_colors: SelectionColors,
        pressed_colors: SelectionColors,
        selection_colors: SelectionColors,
        state_style: StateStyle[]
    },
    text_props: {
        color: string,
        gradient_direction: string,
        format: string,
        font_name: string,
        font_size: number,
        font_weight: number,
        font_style: number,
        text_decoration: number,
        text_transform: number,
        alignment: number,
        view_type: number,
        x_indent: number,
        y_indent: number,
        shadow: string,
        sample_data: string,
        font_url: string,
        css_style: string,
    },
    image_props: {
        format: string,
        alignment: number,
        view_type: number,
        transparency: number,
        sample_data: string,
        css_style: string,
        hide_unexisting: boolean | undefined,
    },
    ticker_props: {
        scroll_speed: number,
        pixels_shift: number,
        queue_child: boolean,
        vertical: boolean,
        wait_time: number,
        start_empty: boolean
    },
    html_props: {
        at_error: DataHtmlErrorHandling
        format: string
        refresh_time: number
    },
    container_props: {
        scale_template: boolean,
        default: string,
        rule_maps: {
            rule: string,
            template: string
        }[]
    },
    decision_props: {
        rule: string,
        display: boolean,
    },
    playlist_props: {
        format: string
    },
    digital_clock_props: {
        locale: string
        time_separator_blink: boolean
        time_zone?: string
        time_zone_offset_minutes?: number
    },
    progress_bar_props: {
        bar_color: string,
        duration: number
    },
    countdown_props: {
        seconds: number,
        data: number,
        countdown_hide: CountdownHide
    },
    analog_clock_props: {
        // Resolved CSS background value (backend-built, like button_props colours). See #c778.
        plate_color?: string,
        hour_hand?: ClockHand,
        minute_hand?: ClockHand,
        second_hand?: ClockHand,
        hour_marker?: ClockMarker,
        minute_marker?: ClockMarker,
        time_zone?: string,
    },
    timer_props: {
        seconds: number,
        show_when_true: boolean,
    },
    function_props: {
        operator: Operator,
        first_argument: string,
        second_argument: string,
        second_channel: number,
        second_row: number,
        second_field: number,
        table_name: string
    },
    editor_props: EditorProps,
    input_text_props: InputTextProps,
    alternating_props: Alternating | null,
    data: any,
    datasheet_colors: string[],
    is_dynamic: boolean,
    style: CSSStyleDeclaration,
    scale: number,
    auto_hide: string,
    // Layout "overlay": the item takes no part in overlap hiding (Renderer.removeOverlap)
    overlay?: boolean,
    auto_size: string,
    viewType: string,
    adoptSubscriptions: any,
    adoptData: Map<string, any>,
    subscription: Subscription,
    ignore_render?: boolean,
    loop?: boolean,
    effects?: Effects,
}

export type Anchor = {
    anchor_id: string,
    anchor_location: string
}

/* see docs/EXTRACTED_COMMENTS.md#c778 */
export type ClockHand = {
    thickness?: number,
    length?: number,
    color?: string,
}

export type ClockMarker = ClockHand;

export type Effects = {
    focus_change_in: RenderEffect[],
    focus_change_out: RenderEffect[],
}

export type EffectDirection =
    | "UP" | "DOWN" | "LEFT" | "RIGHT"
    | "UP_LEFT" | "UP_RIGHT" | "DOWN_LEFT" | "DOWN_RIGHT";

export type RenderEffect = {
    effect_time: number,
    effect_sequence: number,
    effect_type: EffectType,
    // see docs/EXTRACTED_COMMENTS.md#c159
    direction?: EffectDirection,
}

export enum EffectType {
    CROSS_FADE = "CROSS_FADE",
    FLY = "FLY",
    CURTAIN = "CURTAIN",
    TURN = "TURN",
    IN = "IN",
    OUT = "OUT",
}

export type SelectionColors = {
    foreground: string,
    background: string,
}

export type SubmitionObject = {
    object_id: string,
    object_name: string,
}

export type Alternating = {
    alternating_time: number | null,
    continuously: boolean | null
}

export enum ButtonType {
    ButtonUpdate = "BUTTON_UPDATE",
    NavButtonPageNext = "NAV_BUTTON_PAGE_NEXT",
    NavButtonPagePrev = "NAV_BUTTON_PAGE_PREV",
    NavButtonSubPageNext = "NAV_BUTTON_SUB_PAGE_NEXT",
    NavButtonSubPagePrev = "NAV_BUTTON_SUB_PAGE_PREV",
    NavButtonShownPagePrev = "NAV_BUTTON_SHOWN_PAGE_PREV",
    NavButtonHomePage = "NAV_BUTTON_HOME_PAGE",
    NavButtonSelectPage = "NAV_BUTTON_SELECT_PAGE",
}

export enum DataHtmlErrorHandling {
    UnknownHandling = "UnknownHandling",
    /// Hides the object
    Hide = "Hide",
    /// Displays an error page
    ShowErrorPage = "ShowErrorPage",
    /// Displays a default URL page
    ShowDefaultUrl = "ShowDefaultUrl",
    /// Still display the object
    Visible = "Visible",
}

export enum CountdownHide {
    NONE = "NONE",
    COUNTDOWN_END_TIME = "COUNTDOWN_END_TIME",
    COUNTDOWN_REACHED = "COUNTDOWN_REACHED",
}

export enum Operator {
    ADD = "ADD",
    SUBTRACT = "SUBTRACT",
    MULTIPLY = "MULTIPLY",
    DIVIDE = "DIVIDE",
    MODULO = "MODULO",
    TZOFFSET = "TZOFFSET",
    TZONE = "TZONE",
}

export type StateStyle = {
    css_style: string,
    state: State
}

export enum State {
    UNKNOWN_STATE       = "UNKNOWN_STATE",
    ACTIVE              = "ACTIVE",
    CHECKED             = "CHECKED",
    DISABLED            = "DISABLED",
    EMPTY               = "EMPTY",
    ENABLED             = "ENABLED",
    FOCUS               = "FOCUS",
    HOVER               = "HOVER",
    INVALID             = "INVALID",
    LINK                = "LINK",
    OPTIONAL            = "OPTIONAL",
    REQUIRED            = "REQUIRED",
    VALID               = "VALID",
    VISITED             = "VISITED",
}

export type EditorProps = {
     output_only?: boolean,
     data?: string,
     display_width?: number,
     display_height?: number,
}

export type ItemProp = {
    props: {
        id: string;
        style?: CSSProperties;
    };
    /** format is used when requesting the playlist */
    format?: string;
};

export type CollisionPair = {
    aId: string;
    bId: string;
};

export type InputTextProps = {
    input_type: InputType,
    max_length: number;
    selection_colors: SelectionColors;
    css_style: string;
    state_style: StateStyle[];
}

export enum InputType {
    INPUT_TYPE_NUMERIC = 1,
    INPUT_TYPE_ALPHANUMERIC = 2,
}

export type DisconnectPolicy = {
    name: string;
    assign_action: DisconnectPolicyAction;
    reboot_action: DisconnectPolicyAction
}

export type DisconnectPolicyAction = {
    action_type: DisconnectPolicyActionType;
    text: string | null;
    image_url: string | null;
    layout_id: number | null;
    after_duration: number;
}

/* A wall-clock time of day, without a date. */
export type TimeOfDay = {
    hour: number;
    minute: number;
    second: number;
}

/* see docs/EXTRACTED_COMMENTS.md#c763 */
export type ScreenshotPolicy = {
    interval: number;
    retention: number;
    offload_timestamp: TimeOfDay;
}

export type DeviceSettings = {
    content_url: string;
    error_page: number;
    default_page: number;
    disconnect_policy: DisconnectPolicy;
    screenshot_policy: ScreenshotPolicy | null;
    allowed_pages: string[];
    status_bar: boolean;
}

export enum DisconnectPolicyActionType {
    LAYOUT = "LAYOUT",
    IMAGE = "IMAGE",
    TEXT = "TEXT"
}

export type DisconnectPolicyActionSource = "assign" | "reboot";

export type DisconnectPolicyDisplayState = {
    visible: boolean;
    text: string | null;
    image_url: string | null;
    source: DisconnectPolicyActionSource | null;
    activated_at: number | null;
}

// see docs/EXTRACTED_COMMENTS.md#c160
export type ChannelIngestState = {
    name: string;
    rows: number;
    expectedRows: number;
    pending: boolean;
};

// see docs/EXTRACTED_COMMENTS.md#c161
export type LoadingDiagnostics = {
    ingest: {
        pendingChannels: number;
        channels: ChannelIngestState[];
    } | null;
    paint: {
        painted: number;
        total: number;
    } | null;
    provision: {
        fontsReady: number;
        fontsTotal: number;
        datasheetsBuilt: number;
        datasheetsTotal: number;
    } | null;
};

export const __dummy = true;